// ** React Imports
import { useState, ElementType, ChangeEvent, SyntheticEvent, useEffect } from 'react'

// ** MUI Imports
import Box from '@mui/material/Box'
import Grid from '@mui/material/Grid'
import Link from '@mui/material/Link'
import Alert from '@mui/material/Alert'
import Select from '@mui/material/Select'
import { styled } from '@mui/material/styles'
import MenuItem from '@mui/material/MenuItem'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import InputLabel from '@mui/material/InputLabel'
import AlertTitle from '@mui/material/AlertTitle'
import IconButton from '@mui/material/IconButton'
import CardContent from '@mui/material/CardContent'
import FormControl from '@mui/material/FormControl'
import Button, { ButtonProps } from '@mui/material/Button'
import SaveAltIcon from '@mui/icons-material/SaveAlt';
import CloseIcon from '@mui/icons-material/Close';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
// ** Icons Imports
import Close from 'mdi-material-ui/Close'

import { client } from 'src/utils/axios-client'
import { cultureOptionsApi, getAccountEndpoint, teamsOptionsApi, tenuresOptionsApi, timezoneOptionsApi, updateAccountEndpoint, userLocationsOptionsApi } from '../../../configs/endpoints'
import { UserDataType } from '../../../context/types'
import { SelectOption } from 'src/types/common';

import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import { useForm, Control, Controller, FieldErrors } from 'react-hook-form'
import { FormHelperText } from '@mui/material'
import ApplicationStore from '../../../stores/ApplicationStore'
import { useParams } from 'react-router-dom'
import SaveAlt from '@mui/icons-material/SaveAlt'
import { v4 as uuid } from 'uuid';
import EmailConfigurationDialog from '../../email-configuration/Components/EmailConfigurationDialog'

const ImgStyled = styled('img')(({ theme }) => ({
    width: 120,
    height: 120,
    marginRight: theme.spacing(6.25),
    borderRadius: theme.shape.borderRadius
}))

const ButtonStyled = styled(Button)<ButtonProps & { component?: ElementType; htmlFor?: string }>(({ theme }) => ({
    [theme.breakpoints.down('sm')]: {
        width: '100%',
        textAlign: 'center'
    }
}))

const ResetButtonStyled = styled(Button)<ButtonProps>(({ theme }) => ({
    marginLeft: theme.spacing(4.5),
    [theme.breakpoints.down('sm')]: {
        width: '100%',
        marginLeft: 0,
        textAlign: 'center',
        marginTop: theme.spacing(4)
    }
}))

const schema = yup.object().shape({
    firstName: yup.string().required(),
    lastName: yup.string().required(),
    timezone: yup.string().required(),
    team: yup.string().required(),
    culture: yup.string().required(),
    tenure: yup.string().required(),
    userLocation: yup.string().required(),
});

const TabAccount = () => {
    // ** State
    const [openAlert, setOpenAlert] = useState<boolean>(true)
    const [imgSrc, setImgSrc] = useState<string>('/images/avatars/1.png')
    const [user, setUser] = useState(undefined as undefined | UserDataType)
    const [timezones, setTimezones] = useState([] as SelectOption[])
    const [cultures, setCultures] = useState([] as SelectOption[])
    const [teams, setTeams] = useState([] as SelectOption[])
    const [tenures, setTenures] = useState([] as SelectOption[])
    const [users, setUsers] = useState([] as SelectOption[])
    const [userLocations, setUserLocations] = useState([] as SelectOption[])
    const [initialUserState, setInitialUserState] = useState(null as null | UserDataType)
    let { userId } = useParams();

    const {
        reset,
        control,
        setError,
        handleSubmit,
        setValue,
        formState: { errors }
    } = useForm({
        defaultValues: user,
        mode: 'onChange',
        resolver: yupResolver(schema)
    })

    useEffect(() => {
        loadUserAndOptions()
    }, [])

    const loadUserAndOptions = async () => {
        try {
            const userResponse = await client.get(getAccountEndpoint, { params: { userId } });

            const timezoneReponse = await client.get(timezoneOptionsApi)
            const cultureReponse = await client.get(cultureOptionsApi)
            const teamsReponse = await client.get(teamsOptionsApi)
            const tenuresReponse = await client.get(tenuresOptionsApi)
            const userLocationsReponse = await client.get(userLocationsOptionsApi)
            const usersResponse = await client.get('/api/options/users');

            setUser(userResponse.data)
            reset(userResponse.data)
            
            if (userResponse.data.avatarFileName) {
                setImgSrc(`/api/admin/users/images/${userResponse.data.avatarFileName}`)
            } else {
                setImgSrc('/images/avatars/1.png')
            }

            setInitialUserState(userResponse.data)
            setTimezones(timezoneReponse.data)
            setCultures(cultureReponse.data)
            setTeams(teamsReponse.data)
            setTenures(tenuresReponse.data)
            setUserLocations(userLocationsReponse.data)
            setUsers(usersResponse.data);
        } catch (err) {

        }
    }

    const onAvatarChange = async (file: ChangeEvent, onChange: any) => {
        const reader = new FileReader()
        const { files } = file.target as HTMLInputElement
        if (files && files.length !== 0) {
            var newId = uuid();

            const form = new FormData();
            form.append("files", files[0], files[0].name);
            form.append("imageId", newId);


            const response2 = await client.post(`/api/admin/users/images`, form);

            const fileExtension = files[0].name.split('.').pop();
            setUser({ ...user, avatarFileName: `${newId}${fileExtension}` } as UserDataType);
            setValue('avatarFileName', `${newId}.${fileExtension}`);
            setImgSrc(`/api/admin/users/images/${newId}.${fileExtension}`)
        }
    }

    const onResetImageSource = async () => {
        setImgSrc('/images/avatars/1.png')
        setUser({ ...user, avatar: null } as UserDataType);
    }

    const onReset = () => {
        if (initialUserState !== null) {
            setUser(initialUserState);

            if (initialUserState.avatarFileName) {
                setImgSrc(`/api/admin/users/images/${initialUserState.avatarFileName}`)
            } else {
                setImgSrc('/images/avatars/1.png')
            }
        }
    }

    const onSubmit = async (data: UserDataType) => {
        try {
            const response = await client.post(updateAccountEndpoint.replace('{id}', data!.id), data)

            if (response.data.succeeded) {
                ApplicationStore.update(s => {
                    s.snackbarMessage = 'Account Settings Saved';
                    s.snackbarOpen = true;
                })
            } else {
                ApplicationStore.update(s => {
                    s.snackbarMessage = 'Error occured saving account settings';
                    s.snackbarOpen = true;
                })
            }
        } catch (err) {
            ApplicationStore.update(s => {
                s.snackbarMessage = 'Error occured saving account settings';
                s.snackbarOpen = true;
            })
        }
    }

    if (!user) return null;

    console.log('err', errors)

    return (
        <CardContent>
            <form>                
                <Grid container spacing={7}>
                    <Grid item xs={12} sx={{ mt: 4.8, mb: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <ImgStyled src={`${imgSrc}`} alt='Profile Pic' />
                            <Box>
                                <ButtonStyled component='label' variant='contained' htmlFor='account-settings-upload-image' size="small" endIcon={<FileUploadIcon />}>
                                    Upload New Photo
                                    <Controller
                                        name='avatar'
                                        control={control}
                                        rules={{ required: true }}
                                        render={({ field: { value, onChange, onBlur } }) => (
                                            <input
                                                hidden
                                                type='file'
                                                onChange={(e) => onAvatarChange(e, onChange)}
                                                accept='image/png, image/jpeg'
                                                id='account-settings-upload-image'
                                            />
                                        )}
                                    />
                                </ButtonStyled>
                                <ResetButtonStyled color='error' variant='outlined' onClick={onResetImageSource} size="small" endIcon={<RestartAltIcon />}>
                                    Reset
                                </ResetButtonStyled>
                                <Typography variant='body2' sx={{ mt: 5 }}>
                                    Allowed PNG or JPEG. Max size of 800K.
                                </Typography>
                            </Box>
                        </Box>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <Controller
                            name='firstName'
                            control={control}
                            rules={{ required: true }}
                            render={({ field: { value, onChange, onBlur } }) => (
                                <TextField fullWidth label='First Name' placeholder='First Name' value={value} onChange={onChange} onBlur={onBlur} size="small" />
                            )}
                        />
                        {errors.firstName && <FormHelperText sx={{ color: 'error.main' }}>{errors.firstName.message}</FormHelperText>}

                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Controller
                            name='lastName'
                            control={control}
                            rules={{ required: true }}
                            render={({ field: { value, onChange, onBlur } }) => (
                                <TextField fullWidth label='Last Name' placeholder='Lasy Name' value={value} onChange={onChange} onBlur={onBlur} size="small" />
                            )}
                        />
                        {errors.lastName && <FormHelperText sx={{ color: 'error.main' }}>{errors.lastName.message}</FormHelperText>}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Controller
                            name='timezone'
                            control={control}
                            rules={{ required: true }}
                            render={({ field: { value, onChange, onBlur } }) => (
                                <FormControl fullWidth size="small">
                                    <InputLabel>Timezone</InputLabel>
                                    <Select value={value} onChange={onChange} onBlur={onBlur} size="small">
                                        {timezones.map(item => <MenuItem key={item.value} value={item.value}>{item.name}</MenuItem>)}
                                    </Select>
                                </FormControl>
                            )}
                        />
                        {errors.timezone && <FormHelperText sx={{ color: 'error.main' }}>{errors.timezone.message}</FormHelperText>}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Controller
                            name='culture'
                            control={control}
                            rules={{ required: true }}
                            render={({ field: { value, onChange, onBlur } }) => (
                                <FormControl fullWidth size="small">
                                    <InputLabel>Culture</InputLabel>
                                    <Select value={value} onChange={onChange} onBlur={onBlur} size="small">
                                        {cultures.map(item => <MenuItem key={item.value} value={item.value}>{item.name}</MenuItem>)}
                                    </Select>
                                </FormControl>
                            )}
                        />
                        {errors.culture && <FormHelperText sx={{ color: 'error.main' }}>{errors.culture.message}</FormHelperText>}
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <Controller
                            name='team'
                            control={control}
                            rules={{ required: true }}
                            render={({ field: { value, onChange, onBlur } }) => (
                                <FormControl fullWidth size="small">
                                    <InputLabel>Team</InputLabel>
                                    <Select value={value} onChange={onChange} onBlur={onBlur} size="small">
                                        {teams.map(item => <MenuItem key={item.value} value={item.value}>{item.name}</MenuItem>)}
                                    </Select>
                                </FormControl>
                            )}
                        />
                        {errors.team && <FormHelperText sx={{ color: 'error.main' }}>{errors.team.message}</FormHelperText>}
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <Controller
                            name='tenure'
                            control={control}
                            rules={{ required: true }}
                            render={({ field: { value, onChange, onBlur } }) => (
                                <FormControl fullWidth size="small">
                                    <InputLabel>Tenure</InputLabel>
                                    <Select value={value} onChange={onChange} onBlur={onBlur} size="small">
                                        {tenures.map(item => <MenuItem key={item.value} value={item.value}>{item.name}</MenuItem>)}
                                    </Select>
                                </FormControl>
                            )}
                        />
                        {errors.tenure && <FormHelperText sx={{ color: 'error.main' }}>{errors.tenure.message}</FormHelperText>}
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <Controller
                            name='userLocation'
                            control={control}
                            rules={{ required: true }}
                            render={({ field: { value, onChange, onBlur } }) => (
                                <FormControl fullWidth size="small">
                                    <InputLabel>User Location</InputLabel>
                                    <Select value={value} onChange={onChange} onBlur={onBlur} size="small">
                                        {userLocations.map(item => <MenuItem key={item.value} value={item.value}>{item.name}</MenuItem>)}
                                    </Select>
                                </FormControl>
                            )}
                        />
                        {errors.userLocation && <FormHelperText sx={{ color: 'error.main' }}>{errors.userLocation.message}</FormHelperText>}
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <Controller
                            name='practiceLeaderId'
                            control={control}
                            rules={{ required: true }}
                            render={({ field: { value, onChange, onBlur } }) => (
                                <FormControl fullWidth size="small">
                                    <InputLabel>Practice Leader</InputLabel>
                                    <Select value={value} onChange={onChange} onBlur={onBlur} size="small">
                                        <MenuItem value=''>None</MenuItem>
                                        {users.filter(s => s.value !== user.id).map(item => <MenuItem key={item.value} value={item.value}>{item.name}</MenuItem>)}
                                    </Select>
                                </FormControl>
                            )}
                        />
                        {errors.practiceLeaderId && <FormHelperText sx={{ color: 'error.main' }}>{errors.practiceLeaderId.message}</FormHelperText>}
                    </Grid>

                    <Grid item xs={12}>
                        <Button variant='contained' sx={{ mr: 3.5 }} onClick={handleSubmit(onSubmit)} size="small" endIcon={<SaveAltIcon />}>
                            Save Changes
                        </Button>
                        <Button type='reset' variant='outlined' color='secondary' onClick={onReset} size="small" endIcon={<CloseIcon />}>
                            Reset
                        </Button>
                    </Grid>
                </Grid>
            </form>
        </CardContent>
    )
}

export default TabAccount
